import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors } from '@angular/common/http';
import { smartAuthInterceptor } from 'app/core/auth/interceptors/smart-auth.interceptor';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, inject } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    Router,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { providePermission } from 'app/core/permission';
import { provideSentry } from 'app/core/sentry/providers/sentry.provider';
import { mockApiServices } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { provideTranslocoMessageformat } from '@ngneat/transloco-messageformat';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslocoLanguageService } from 'app/core/i18n/transloco-language.service';
import { RequestInterceptor } from './core/interceptors/request.interceptor';
import { NgxPermissionsModule } from 'ngx-permissions';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        // Solo el interceptor funcional, sin withInterceptorsFromDi(): es el comportamiento
        // efectivo que ya había. provideAuth() registraba antes su propio provideHttpClient
        // y pisaba a este, con lo cual RequestInterceptor nunca llegaba a correr. Se deja
        // igual para no activar de golpe un interceptor que lleva dormido y que además
        // duplicaría la cabecera Authorization.
        provideHttpClient(withInterceptors([smartAuthInterceptor])),

        ...provideSentry({
            showErrorDialog: false,
            logErrors: true
        }),

        {
            provide: HTTP_INTERCEPTORS,
            useClass: RequestInterceptor,
            multi: true,
        },

        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),
        importProvidersFrom(NgxPermissionsModule.forRoot()),

        // Permissions (RBAC)
        ...providePermission(),
        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    // {
                    //     id: 'tr',
                    //     label: 'Turkish',
                    // },
                    {
                        id: 'es',
                        label: 'Español',
                    },
                ],
                defaultLang: 'es',
                fallbackLang: 'es',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        provideTranslocoMessageformat(),

        // El unico punto donde se nombra el motor de traduccion: los consumidores
        // inyectan LanguageService.
        { provide: LanguageService, useClass: TranslocoLanguageService },
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                // construirlo registra la t() sin DI que usan los colDef
                inject(LanguageService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi: true,
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
